import { common } from '../../i18n/common.en';

/** Relative time for feeds and "last test" labels. */
export function relativeTime(iso: string | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const diffMin = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (diffMin < 1) return common.time.justNow;
  if (diffMin < 60) return common.time.minutesAgo(diffMin);
  const h = Math.round(diffMin / 60);
  if (h < 24) return common.time.hoursAgo(h);
  const d = Math.round(h / 24);
  if (d === 1) return common.time.yesterday;
  return common.time.daysAgo(d);
}

/** "in 9 h 40 m" style countdown for a future instant; "now" once it has passed. */
export function timeUntil(iso: string | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - now;
  if (ms <= 30_000) return common.time.now;
  const min = Math.round(ms / 60_000);
  if (min < 60) return common.time.inMinutes(min);
  const h = Math.floor(min / 60);
  if (h < 24) return common.time.inHours(h, min % 60);
  return common.time.inDays(Math.round(h / 24));
}

export function clockTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function initials(nameOrEmail: string): string {
  const base = nameOrEmail.split('@')[0] ?? nameOrEmail;
  const parts = base.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function hostOf(url: string): string {
  const u = url.replace(/%20/g, ' ').trim();
  try {
    return new URL(u.startsWith('http') ? u : `https://${u}`).host;
  } catch {
    return url;
  }
}

export function sum(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

export function formatMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
}

/** Time-of-day greeting; the dashboard must not say "Good morning" at 6pm. */
export function greeting(name: string, hour = new Date().getHours()): string {
  if (hour < 12) return common.greeting.morning(name);
  if (hour < 18) return common.greeting.afternoon(name);
  return common.greeting.evening(name);
}
